import { Link, useLocation, useSearchParams } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cn } from "src/lib/utils";

interface BreadcrumbItem {
	label: string;
	path?: string;
}

const routeBreadcrumbs: Record<string, BreadcrumbItem[]> = {
	// List pages
	"/admin/vendor-sponsorship/homepage-carousel": [
		{ label: "Homepage" },
		{ label: "Carousel" }
	],
	"/admin/vendor-sponsorship/full-screen-takeover": [
		{ label: "Homepage" },
		{ label: "Takeover" }
	],
	"/admin/vendor-sponsorship/promotion": [
		{ label: "Homepage" },
		{ label: "Promotions" }
	],
	"/admin/vendor-sponsorship/app-branding": [
		{ label: "Homepage" },
		{ label: "Branding" }
	],
	"/admin/social-feed": [{ label: "Social Feed" }],
	"/admin/analytics": [{ label: "Analytics" }],
	"/admin/announcements": [{ label: "Announcements" }],
	"/admin/announcements/create": [
		{
			label: "Announcements",
			path: "/admin/announcements"
		},
		{ label: "New Announcement" }
	],
	"/admin/announcements/": [
		{
			label: "Announcements",
			path: "/admin/announcements"
		},
		{ label: "Edit Announcement" }
	],
	"/admin/social-feed/hashtags": [
		{ label: "Social Feed" },
		{ label: "Hashtags" }
	],
	"/admin/social-feed/hashtags/create": [
		{
			label: "Hashtags",
			path: "/admin/social-feed/hashtags"
		},
		{ label: "New Hashtag" }
	],
	"/admin/social-feed/hashtags/": [
		{
			label: "Hashtags",
			path: "/admin/social-feed/hashtags"
		},
		{ label: "Edit Hashtag" }
	],
	"/admin/social-feed/favourite-feeds": [
		{ label: "Social Feed" },
		{ label: "Favourites" }
	],
	"/admin/app/events": [{ label: "Events" }],
	"/admin/app/events/create": [
		{
			label: "Events",
			path: "/admin/app/events"
		},
		{ label: "New Event" }
	],
	"/admin/app/events/": [
		{
			label: "Events",
			path: "/admin/app/events"
		},
		{ label: "Edit Event" }
	],
	"/admin/app/pages": [{ label: "Pages" }],
	"/admin/app/pages/create": [
		{
			label: "Pages",
			path: "/admin/app/pages"
		},
		{ label: "New Page" }
	],
	"/admin/app/pages/": [
		{
			label: "Pages",
			path: "/admin/app/pages"
		},
		{ label: "Edit Page" }
	],
	"/admin/app/news": [{ label: "News" }],
	"/admin/app/news/create": [
		{
			label: "News",
			path: "/admin/app/news"
		},
		{ label: "New Article" }
	],
	"/admin/app/news/": [
		{
			label: "News",
			path: "/admin/app/news"
		},
		{ label: "Edit Article" }
	],
	"/admin/recognition/types/create": [
		{
			label: "Types",
			path: "/admin/recognition/types"
		},
		{ label: "New Type" }
	],
	"/admin/recognition/types/": [
		{
			label: "Types",
			path: "/admin/recognition/types"
		},
		{ label: "Edit Type" }
	],
	"/admin/recognition/types": [{ label: "Recognition" }, { label: "Types" }],
	"/admin/recognition/settings": [
		{ label: "Recognition" },
		{ label: "Settings" }
	],
	"/admin/recognition": [{ label: "Recognition" }, { label: "Recognitions" }],
	"/admin/app/homepage/quick-links": [
		{ label: "Homepage" },
		{ label: "Quick Links" }
	],
	"/admin/app/homepage/quick-links/create": [
		{
			label: "Quick Links",
			path: "/admin/app/homepage/quick-links"
		},
		{ label: "New Quick Link" }
	],
	"/admin/operations/forms": [{ label: "Operations" }, { label: "Forms" }],
	"/admin/operations/reports": [{ label: "Operations" }, { label: "Reports" }],
	"/admin/operations/reports/form/": [
		{ label: "Operations" },
		{ label: "Reports", path: "/admin/operations/reports" },
		{ label: "Report" }
	],
	// Create/Edit pages
	"/admin/vendor-sponsorship/homepage-carousel/banners/new": [
		{
			label: "Carousel",
			path: "/admin/vendor-sponsorship/homepage-carousel/banners?published=true"
		},
		{ label: "New Banner" }
	],
	"/admin/vendor-sponsorship/homepage-carousel/banners/edit": [
		{
			label: "Carousel",
			path: "/admin/vendor-sponsorship/homepage-carousel/banners?published=true"
		},
		{ label: "Edit Banner" }
	],
	"/admin/vendor-sponsorship/full-screen-takeover/banners/new": [
		{
			label: "Takeover",
			path: "/admin/vendor-sponsorship/full-screen-takeover/banners?published=true"
		},
		{ label: "New Takeover" }
	],
	"/admin/vendor-sponsorship/full-screen-takeover/banners/edit": [
		{
			label: "Takeover",
			path: "/admin/vendor-sponsorship/full-screen-takeover/banners?published=true"
		},
		{ label: "Edit Takeover" }
	],
	"/admin/vendor-sponsorship/promotion/banners/new": [
		{
			label: "Promotions",
			path: "/admin/vendor-sponsorship/promotion/banners?published=true"
		},
		{ label: "New Promotion" }
	],
	"/admin/vendor-sponsorship/promotion/banners/edit": [
		{
			label: "Promotions",
			path: "/admin/vendor-sponsorship/promotion/banners?published=true"
		},
		{ label: "Edit Promotion" }
	],
	"/admin/vendor-sponsorship/app-branding/brands/new-app-branding-form": [
		{
			label: "Branding",
			path: "/admin/vendor-sponsorship/app-branding/brands?published=true"
		},
		{ label: "New Brand" }
	],
	"/admin/vendor-sponsorship/app-branding/brands/edit": [
		{
			label: "Branding",
			path: "/admin/vendor-sponsorship/app-branding/brands?published=true"
		},
		{ label: "Edit Brand" }
	],
	"/admin/app/homepage/quick-links/": [
		{
			label: "Quick Links",
			path: "/admin/app/homepage/quick-links"
		},
		{ label: "Edit Quick Link" }
	],
	"/admin/app/homepage/key-shouts": [
		{ label: "Homepage" },
		{ label: "Key Shouts" }
	],
	"/admin/app/homepage/ordering": [
		{ label: "Homepage" },
		{ label: "Ordering" }
	],
	"/admin/app/homepage/section-titles": [
		{ label: "Homepage" },
		{ label: "Section Titles" }
	],
	"/admin/app/navigation": [{ label: "Navigation" }],
	"/admin/app/homepage/key-shouts/create": [
		{
			label: "Key Shouts",
			path: "/admin/app/homepage/key-shouts"
		},
		{ label: "New Key Shout" }
	],
	"/admin/app/homepage/key-shouts/": [
		{
			label: "Key Shouts",
			path: "/admin/app/homepage/key-shouts"
		},
		{ label: "Edit Key Shout" }
	],
	"/admin/operations/forms/edit": [
		{ label: "Operations" },
		{ label: "Forms", path: "/admin/operations/forms" },
		{ label: "Edit Form" }
	],
	"/admin/operations/forms/edit-questions": [
		{ label: "Operations" },
		{ label: "Forms", path: "/admin/operations/forms" },
		{ label: "Edit Questions" }
	],
	"/admin/operations/forms/new": [
		{ label: "Operations" },
		{ label: "Forms", path: "/admin/operations/forms" },
		{ label: "New Form" }
	],
	"/admin/users/people": [{ label: "Users" }, { label: "People" }],
	"/admin/users/people/": [
		{ label: "Users" },
		{ label: "People", path: "/admin/users/people" },
		{ label: "Person" }
	],
	"/admin/users/groups": [{ label: "Users" }, { label: "Groups" }],
	"/admin/users/groups/create": [
		{ label: "Users" },
		{ label: "Groups", path: "/admin/users/groups" },
		{ label: "New Group" }
	],
	"/admin/users/groups/": [
		{ label: "Users" },
		{ label: "Groups", path: "/admin/users/groups" },
		{ label: "Edit Group" }
	],
	"/admin/users/locations": [{ label: "Users" }, { label: "Locations" }],
	"/admin/users/permissions": [{ label: "Users" }, { label: "Permissions" }],
	"/admin/users/permissions/create": [
		{ label: "Users" },
		{ label: "Permissions", path: "/admin/users/permissions" },
		{ label: "New Role" }
	],
	"/admin/users/permissions/": [
		{ label: "Users" },
		{ label: "Permissions", path: "/admin/users/permissions" },
		{ label: "Edit Role" }
	],
	"/admin/users/locations/regions/create": [
		{ label: "Users" },
		{ label: "Locations", path: "/admin/users/locations" },
		{ label: "New Region" }
	],
	"/admin/users/locations/regions/": [
		{ label: "Users" },
		{ label: "Locations", path: "/admin/users/locations" },
		{ label: "Edit Region" }
	],
	"/admin/users/locations/areas/create": [
		{ label: "Users" },
		{ label: "Locations", path: "/admin/users/locations" },
		{ label: "New Area" }
	],
	"/admin/users/locations/areas/": [
		{ label: "Users" },
		{ label: "Locations", path: "/admin/users/locations" },
		{ label: "Edit Area" }
	],
	"/admin/users/locations/stores/create": [
		{ label: "Users" },
		{ label: "Locations", path: "/admin/users/locations" },
		{ label: "New Store" }
	],
	"/admin/users/locations/stores/": [
		{ label: "Users" },
		{ label: "Locations", path: "/admin/users/locations" },
		{ label: "Edit Store" }
	]
};

function matchBreadcrumbs(pathname: string): BreadcrumbItem[] | null {
	// Exact match first
	if (routeBreadcrumbs[pathname]) return routeBreadcrumbs[pathname];

	// Prefix match (for paths with dynamic segments like /edit/123)
	const match = Object.entries(routeBreadcrumbs)
		.sort(([a], [b]) => b.length - a.length)
		.find(([path]) => pathname.startsWith(path));

	return match ? match[1] : null;
}

const FORMS_LIST_PATH = "/admin/operations/forms";
const REPORTS_LIST_PATH = "/admin/operations/reports";

export function Breadcrumbs() {
	const { pathname } = useLocation();
	const [searchParams] = useSearchParams();
	const items = matchBreadcrumbs(pathname);

	if (!items) return null;

	// The forms-in-category and reports-in-category views reuse their list
	// routes with a `?category=` param, so the static map alone can't
	// distinguish "Forms"/"Reports" (category list) from "Forms > <category>".
	// Append the category as a trailing crumb and make the list crumb a link
	// back to the category list.
	// `useSearchParams().get` already URL-decodes, so `category` is the plain
	// category name; re-encode only when building link hrefs.
	const category = searchParams.get("category");
	const normalizedPathname = pathname.replace(/\/+$/, "");
	const isCategoryListPath =
		normalizedPathname === FORMS_LIST_PATH ||
		normalizedPathname === REPORTS_LIST_PATH;
	const isReportsFormPath = normalizedPathname.startsWith(
		`${REPORTS_LIST_PATH}/form/`
	);
	const listPath =
		normalizedPathname === REPORTS_LIST_PATH
			? REPORTS_LIST_PATH
			: FORMS_LIST_PATH;

	let withCategory = items;
	if (category) {
		if (isCategoryListPath) {
			// A category's form list reuses the list route with ?category — show
			// the category as the trailing crumb; the list crumb links back.
			withCategory = [
				...items.slice(0, -1),
				{ ...items[items.length - 1], path: listPath },
				{ label: category }
			];
		} else if (isReportsFormPath) {
			// A specific form's report — insert the owning category (linking back
			// to that category's report list) before the final "Report" crumb.
			withCategory = [
				...items.slice(0, -1),
				{
					label: category,
					path: `${REPORTS_LIST_PATH}/?category=${encodeURIComponent(category)}`
				},
				items[items.length - 1]
			];
		}
	}

	return (
		<nav aria-label="Breadcrumb" className="text-caption mb-1">
			<ol className="flex items-center gap-1.5">
				{withCategory.map((item, index) => (
					<li key={item.label} className="flex items-center gap-1.5">
						{index > 0 && (
							<ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
						)}
						{item.path ? (
							<Link
								to={item.path}
								className="hover:text-foreground transition-colors duration-150"
							>
								{item.label}
							</Link>
						) : (
							<span
								className={cn(
									index === withCategory.length - 1 &&
										"text-caption text-foreground"
								)}
								{...(index === withCategory.length - 1
									? { "aria-current": "page" as const }
									: {})}
							>
								{item.label}
							</span>
						)}
					</li>
				))}
			</ol>
		</nav>
	);
}
